import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import { BackToDetailsLink } from '@/features/deviceDetails/BackToDetailsLink'
import { fetchAlarmTopics } from '@/features/alarmTopics/api'
import { fetchSystemSubscriptions, type TopicState } from '@/features/systemAlarmSubscriptions/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/system_alarm_subscriptions_overview.html — read-only,
 * per-severity view of one system's effective alarm subscriptions
 * (Inherited from fleet default vs. Custom override). A `?controller_id=`
 * query param preselects the controller, matching the legacy per-device
 * sidebar's direct link. Grouping is done client-side from the flat
 * GET /portal/settings/alarm-topics response (same grouping the legacy
 * server-side _system_topics_view() does) combined with
 * GET /portal/settings/alarms/<id>/subscriptions's per-topic effective
 * state - both endpoints already existed before this page did.
 */
export default function SystemAlarmSubscriptionsPage() {
  // Read directly from the URL, not frozen into local state at mount -- see
  // SettingsAlarmsPage's matching comment for the real bug this fixes.
  const [searchParams, setSearchParams] = useSearchParams()
  const controllerId = searchParams.get('controller_id') ?? ''
  // The site the user actually navigated here FROM -- see SettingsAlarmsPage's
  // matching comment. "← Back to site details" always returns to this one,
  // never whichever site the dropdown currently shows.
  const [originalControllerId] = useState(() => searchParams.get('controller_id') ?? '')

  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  const topicsQuery = useQuery({ queryKey: ['alarm-topics'], queryFn: fetchAlarmTopics })
  const subsQuery = useQuery({
    queryKey: ['system-subscriptions', controllerId],
    queryFn: () => fetchSystemSubscriptions(controllerId),
    enabled: Boolean(controllerId),
  })

  const selectedController = controllersQuery.data?.find((c) => c.controller_id === controllerId)

  const groups = useMemo(() => {
    const map = new Map<string, ({ topic: (typeof topics)[number] } & TopicState)[]>()
    const topics = topicsQuery.data?.alarm_topics ?? []
    for (const topic of topics) {
      const state = subsQuery.data?.topics[String(topic.alarm_topic_id)] ?? { is_subscribed: false, is_custom: false }
      const key = topic.alarm_severity_name || 'Uncategorized'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push({ topic, ...state })
    }
    return map
  }, [topicsQuery.data, subsQuery.data])

  return (
    <div className="space-y-4">
      <BackToDetailsLink controllerId={originalControllerId} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-text-primary">
          Subscriptions{selectedController && <> — <span className="text-text-secondary">{selectedController.controller_name}</span></>}
        </h1>
        {controllerId && (
          <Link
            to={`/system-alarm-subscriptions/manage?controller_id=${controllerId}`}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Manage Subscriptions
          </Link>
        )}
      </div>

      <div className="rounded border border-border bg-card p-4">
        <label className="mb-1 block text-xs text-text-secondary">Controller</label>
        <select
          value={controllerId}
          onChange={(e) => setSearchParams(e.target.value ? { controller_id: e.target.value } : {})}
          className="w-full max-w-sm rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
        >
          <option value="">Select a controller…</option>
          {(controllersQuery.data ?? []).map((c) => (
            <option key={c.controller_id} value={c.controller_id}>
              {c.controller_name || c.controller_id}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-text-secondary">
        Read-only view of your current alarm subscriptions on this system. Each topic is either{' '}
        <strong>Inherited</strong> (from your severity subscription for this system, or else your fleet default) or{' '}
        <strong>Custom</strong>. To make changes, use Manage Subscriptions.
      </p>

      {!controllerId && null}
      {controllerId && subsQuery.isLoading && <LoadingState label="Loading subscriptions…" />}
      {controllerId && subsQuery.isError && (
        <ErrorState
          message={subsQuery.error instanceof Error ? subsQuery.error.message : 'Failed to load.'}
          onRetry={() => subsQuery.refetch()}
        />
      )}

      {controllerId && subsQuery.data && groups.size === 0 && (
        <p className="text-sm text-text-secondary">No alarm topics are configured for this controller.</p>
      )}

      {controllerId && subsQuery.data && groups.size > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from(groups.entries()).map(([severityName, entries]) => {
            const subscribedCount = entries.filter((e) => e.is_subscribed).length
            return (
              <div key={severityName} className="rounded border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                  <span className="text-sm font-semibold text-text-primary">{severityName}</span>
                  <span className="rounded-full bg-bg-secondary px-2 py-0.5 text-xs text-text-secondary">
                    {subscribedCount}/{entries.length} subscribed
                  </span>
                </div>
                <div className="p-2">
                  {entries.map((entry) => (
                    <div
                      key={entry.topic.alarm_topic_id}
                      className="flex items-center justify-between border-b border-border py-1 text-sm last:border-0"
                    >
                      <span className={entry.is_subscribed ? 'text-brand' : 'text-text-tertiary'}>
                        {entry.topic.alarm_real_name}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          entry.is_custom ? 'bg-accent/20 text-accent' : 'bg-bg-tertiary text-text-secondary'
                        }`}
                        title={
                          entry.is_custom
                            ? 'You have a local override for this topic'
                            : entry.source === 'site_severity'
                              ? `Following your ${severityName} subscription for this system`
                              : `Following your fleet default for ${severityName}`
                        }
                      >
                        {entry.is_custom ? 'Custom' : 'Inherited'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
